import { toReplyDraftError } from './errors';
import {
  toReplyDraft,
  toReplyDraftSummary,
  toSourcePublication,
  type WireReplyDraft,
} from './mappers';
import { ReplyDraftError } from '../model/errors';
import type { ReplyDraftsGateway } from '../ports/replyDraftsGateway';

import { fetchClient } from '@/util/fetchClient';

function draftOf(data: { data?: WireReplyDraft } | undefined) {
  if (!data?.data) {
    throw new ReplyDraftError('unknown', 'Reply draft response had no draft');
  }

  return toReplyDraft(data.data);
}

/**
 * Runs a request and rethrows its failure as a `ReplyDraftError`, so every
 * caller handles one error type whatever the transport threw.
 */
async function request<Result>(run: () => Promise<Result>): Promise<Result> {
  try {
    return await run();
  } catch (error) {
    throw toReplyDraftError(error);
  }
}

const path = (id: string) => ({ params: { path: { id } } });

/** Reply drafts' backend, over the generated API client. Analytics are the hooks' job, not this. */
export const fetchReplyDraftsGateway: ReplyDraftsGateway = {
  listOpen: () =>
    request(async () => {
      const { data } = await fetchClient.GET('/v2/expert/reply-drafts');

      return (data?.data ?? []).map(toReplyDraftSummary);
    }),

  get: (replyDraftId) =>
    request(async () => {
      const { data } = await fetchClient.GET('/v2/expert/reply-drafts/{id}', path(replyDraftId));

      return draftOf(data);
    }),

  select: (replyDraftId, stateVersion, selection) =>
    request(async () => {
      const { data } = await fetchClient.POST('/v2/expert/reply-drafts/{id}/selection', {
        ...path(replyDraftId),
        body: { stateVersion, ...selection },
      });

      return draftOf(data);
    }),

  applyEvents: (replyDraftId, stateVersion, events) =>
    request(async () => {
      const { data } = await fetchClient.POST('/v2/expert/reply-drafts/{id}/events', {
        ...path(replyDraftId),
        body: { stateVersion, events },
      });

      return draftOf(data);
    }),

  addNote: (replyDraftId, stateVersion, text) =>
    request(async () => {
      const { data } = await fetchClient.POST('/v2/expert/reply-drafts/{id}/voice', {
        ...path(replyDraftId),
        body: { stateVersion, transcript: text },
      });

      return draftOf(data);
    }),

  useDraft: (replyDraftId, stateVersion, acknowledgement, signal) =>
    request(async () => {
      const { data } = await fetchClient.POST('/v2/expert/reply-drafts/{id}/use', {
        ...path(replyDraftId),
        body: { stateVersion, ...acknowledgement },
        signal,
      });

      if (!data?.data) {
        throw new ReplyDraftError('unknown', 'Use draft response had no text');
      }

      return { composedText: data.data.composedText };
    }),

  getPublication: (publicationId) =>
    request(async () => {
      const { data } = await fetchClient.GET('/v2/publications/{id}', {
        params: { path: { id: publicationId } },
      });

      if (!data?.data) {
        throw new ReplyDraftError('unknown', 'Publication response had no publication');
      }

      return toSourcePublication(data.data);
    }),
};
